import { useUserEventLogger } from '@repo/tracking-service';
import cn from 'clsx';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { PreloadedQuery, usePreloadedQuery } from 'react-relay';

import { employmentContractsQuery, EmploymentContractsType } from '@/__generated__/employmentContractsQuery.graphql';
import { SurveyTopNavigation } from '@/components/survey/common/TopNavigation';
import { useSurveyUserEventAttributes } from '@/components/survey/common/hooks/use-survey-user-event-attributes';
import { CTA, CheckList, CheckListGroup } from '@/components/survey/common/ui';
import common from '@/components/survey/common/ui/styles/common.module.scss';
import { ContractsIndicator } from '@/components/survey/employment/contracts/ContractsIndicator';
import { updateEmploymentContractsQuery } from '@/graphql/mutation/survey/employment';
import { contractsQuery } from '@/graphql/query/survey/employment';
import { SURVEY_PAGES } from '@/lib/constants/paths';
import { useUpsertEmployment } from '@/lib/hooks/api/mutation/survey/use-upsert-employment';
import { someCompletedBusinessSurveyAtom } from '@/lib/stores/survey/common';

const answers: { value: EmploymentContractsType; label: string }[] = [
  { value: 'all', label: '모두 제출할 수 있어요' },
  { value: 'some', label: '일부만 제출할 수 있어요' },
  { value: 'none', label: '제출할 수 없어요' }
];

export const ContractsContainer = ({ queryRef }: { queryRef: PreloadedQuery<employmentContractsQuery> }) => {
  const { getSurveyEmploymentAnswer } = usePreloadedQuery<employmentContractsQuery>(contractsQuery, queryRef);
  const router = useRouter();
  const { sendClickEvent } = useUserEventLogger();
  const eventAttributes = useSurveyUserEventAttributes();
  const [upsertEmploymentAnswer, isLoading] = useUpsertEmployment(updateEmploymentContractsQuery);
  const someCompletedAnswer = useAtomValue(someCompletedBusinessSurveyAtom);

  const [selectedAnswer, setSelectedAnswer] = useState<EmploymentContractsType>('blank');

  useEffect(() => {
    const prevAnswer = getSurveyEmploymentAnswer.employmentAnswer?.hasEmploymentContracts || 'blank';
    if (selectedAnswer === 'blank' && prevAnswer !== 'blank') {
      setSelectedAnswer(prevAnswer);
    }
  }, [getSurveyEmploymentAnswer.employmentAnswer?.hasEmploymentContracts, selectedAnswer]);

  return (
    <>
      <SurveyTopNavigation needConfirm leftClickEvent={'survey_employment-contract_back'} />
      <main className={cn(common.main_layout, common.main_cta_space)}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            sendClickEvent('survey_employment-contract_confirm', eventAttributes);

            upsertEmploymentAnswer({
              variables: {
                hasEmploymentContracts: selectedAnswer
              },
              onCompleted: () => {
                if (someCompletedAnswer) {
                  router.push(SURVEY_PAGES.사업장별질문);
                  return;
                }
                router.push(SURVEY_PAGES.intro);
              }
            });
          }}
        >
          <ContractsIndicator />
          <h2 className={common.content_title}>
            갖고 계신 근로계약서를
            <br />
            모두 제출하실 수 있으신가요?
            <span className={'text-16 mt-3 block font-normal leading-[23px] text-gray-700'}>
              지금 바로 제출하지 못해도 괜찮아요.
              <br />
              제출 방법은 알림톡으로 알려드릴게요.
            </span>
          </h2>

          <CheckListGroup>
            {answers?.map(({ value, label }) => (
              <CheckList
                key={value}
                value={value}
                checked={selectedAnswer === value}
                onChange={() => {
                  sendClickEvent(`survey_employment-contract_${value}`, eventAttributes);
                  setSelectedAnswer(value);
                }}
              >
                {label}
              </CheckList>
            ))}
          </CheckListGroup>

          <section className={'pb-5 pt-6'}>
            <h3 className={common.section_title}>근로계약서, 왜 확인이 필요한가요?</h3>
            <ul className={'flex flex-col gap-2'}>
              <li className={common.section_list_item}>국세청 검토 단계에서 관할 세무서가 근로계약서를 요구할 수 있기 때문에 필요해요.</li>
              <li className={common.section_list_item}>
                근로계약서가 있으면 단기 근로자나 1년 미만 근로자에 대해서 환급금을 더 받을 수 있는지 검토할 수 있어요.
              </li>
            </ul>
          </section>

          {selectedAnswer !== 'blank' && (
            <CTA disabled={isLoading} type={'submit'}>
              다음
            </CTA>
          )}
        </form>
      </main>
    </>
  );
};
